import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags, truncateAtSentence } from './helpers';

export const arcticFrost: TemplateRegistration = {
  meta: {
    id: 'arctic-frost',
    name: 'Arctic Frost',
    category: 'Modern',
    description: 'Cool-toned dashboard layout with frosted glass panels',
    previewImage: 'https://images.unsplash.com/photo-1478719059408-592965723cbc?w=800&q=80',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeHeadline = sanitizeText(data.headline);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const heroImage = photos[0] || null;
    const galleryImages = photos.slice(1, 6);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['Rajdhani', 'Nunito']);

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="google-reviews">
        <div class="container">
          <div class="review-badge">
            <div class="review-stars">${renderStars(Math.round(rating))}</div>
            <span class="review-text">${rating.toFixed(1)} out of 5 &mdash; ${reviewCount} Google reviews</span>
          </div>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="promo-banner">
        <div class="container">
          <div class="promo-inner">
            <span class="promo-badge">${promoDiscount}</span>
            <div class="promo-content">
              <strong>${promoTitle}</strong>
              <span>${promoDesc}</span>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      sections.push(`
      <section id="about" class="section section-dark">
        <div class="container">
          <div class="frost-panel">
            <h2 class="section-title">About Us</h2>
            <p class="about-text">${safeAbout}</p>
          </div>
        </div>
      </section>`);
    }

    // --- Services ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const serviceCards = data.services.map(s => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        return `
          <div class="service-card frost-panel">
            <div class="service-icon">${getIconSvg(s.icon || 'star')}</div>
            <h3>${sName}</h3>
            <p>${sDesc}</p>
            ${s.price ? `<span class="service-price">${sanitizeText(s.price)}</span>` : ''}
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="section section-darker">
        <div class="container">
          <h2 class="section-title">Our Services</h2>
          <div class="services-grid">${serviceCards}</div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map(url => `<div class="gallery-item"><img src="${escapeAttr(url)}" alt="${safeName}" loading="lazy"></div>`).join('');
      sections.push(`
      <section id="gallery" class="section section-dark">
        <div class="container">
          <h2 class="section-title">Gallery</h2>
          <div class="gallery-grid">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const cards = data.testimonials.map(t => {
        const tName = sanitizeText(t.name);
        const tContent = sanitizeText(t.content);
        const tRole = t.role ? sanitizeText(t.role) : '';
        return `
          <div class="testimonial-card frost-panel">
            <div class="testimonial-body">
              <p>&ldquo;${tContent}&rdquo;</p>
            </div>
            <div class="testimonial-author">
              <strong>${tName}</strong>
              ${tRole ? `<span>${tRole}</span>` : ''}
              ${t.rating ? `<div class="testimonial-stars">${renderStars(t.rating)}</div>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="testimonials" class="section section-darker">
        <div class="container">
          <h2 class="section-title">What People Say</h2>
          <div class="testimonials-grid">${cards}</div>
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const items = data.faq.map(f => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        return `
          <div class="faq-item frost-panel">
            <h3 class="faq-q">${q}</h3>
            <p class="faq-a">${a}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="faq" class="section section-dark">
        <div class="container">
          <h2 class="section-title">FAQ</h2>
          <div class="faq-list">${items}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const rows = data.priceList.map(p => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        return `
          <div class="price-row frost-panel">
            <div class="price-info">
              <h3>${pName}</h3>
              <p>${pDesc}</p>
            </div>
            <span class="price-amount">${pPrice}</span>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="section section-darker">
        <div class="container">
          <h2 class="section-title">Pricing</h2>
          <div class="price-list">${rows}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="section section-dark">
        <div class="container">
          <h2 class="section-title">Hours</h2>
          <ul class="hours-list">${hoursList}</ul>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="section section-darker">
        <div class="container">
          <h2 class="section-title">Get In Touch</h2>
          <div class="contact-grid">
            <div class="contact-info">
              ${enabledFeatures.has('phone-display') && phone ? `<div class="contact-item">${getIconSvg('phone')}<a href="tel:${escapeAttr(phone)}">${sanitizeText(phone)}</a></div>` : ''}
              ${enabledFeatures.has('address-map') && address ? `<div class="contact-item">${getIconSvg('map-pin')}<span>${sanitizeText(address)}</span></div>` : ''}
              ${enabledFeatures.has('address-map') && googleMapsUrl ? `<a class="map-link" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">View on Google Maps</a>` : ''}
            </div>
            <form class="contact-form frost-panel" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
              <input type="text" placeholder="Your Name" required>
              <input type="email" placeholder="Your Email" required>
              <textarea placeholder="Message" rows="4" required></textarea>
              <button type="submit">Send Message</button>
            </form>
          </div>
        </div>
      </section>`);
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --primary: #4FC3F7; --bg: #37474F; --bg-alt: #263238; --accent: #4FC3F7;
      --text: #ECEFF1; --text-muted: #90A4AE;
      --font-heading: 'Rajdhani', sans-serif; --font-body: 'Nunito', sans-serif;
      --frost-bg: rgba(255,255,255,.06); --frost-border: rgba(255,255,255,.1);
    }
    html { scroll-behavior: smooth; }
    body { font-family: var(--font-body); background: var(--bg); color: var(--text); line-height: 1.6; }
    .container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }
    a { color: var(--accent); text-decoration: none; transition: opacity .2s; }
    a:hover { opacity: .8; }

    /* FROST PANEL */
    .frost-panel { background: var(--frost-bg); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px); border: 1px solid var(--frost-border); border-radius: 12px; padding: 32px; }

    /* NAV */
    .navbar { position: fixed; top: 0; left: 0; right: 0; z-index: 100; background: rgba(38,50,56,.9); backdrop-filter: blur(14px); -webkit-backdrop-filter: blur(14px); border-bottom: 1px solid var(--frost-border); }
    .nav-inner { display: flex; align-items: center; justify-content: space-between; padding: 14px 24px; max-width: 1200px; margin: 0 auto; }
    .nav-brand { font-family: var(--font-heading); font-size: 1.6rem; color: var(--text); font-weight: 700; letter-spacing: 1px; }
    .nav-links { display: flex; gap: 24px; list-style: none; }
    .nav-links a { color: var(--text-muted); font-size: .85rem; text-transform: uppercase; letter-spacing: 1.5px; font-weight: 600; }
    .nav-links a:hover { color: var(--accent); }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .nav-toggle span { display: block; width: 26px; height: 2px; background: var(--accent); margin: 5px 0; transition: .3s; border-radius: 1px; }

    /* HERO */
    .hero { position: relative; min-height: 100vh; display: flex; align-items: center; justify-content: center; overflow: hidden; }
    .hero-bg { position: absolute; inset: 0; background-size: cover; background-position: center; opacity: .35; }
    .hero-overlay { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(38,50,56,.6) 0%, var(--bg) 100%); }
    .hero-content { position: relative; z-index: 2; padding: 120px 24px 80px; width: 100%; display: flex; justify-content: center; }
    .hero-glass { background: rgba(255,255,255,.08); backdrop-filter: blur(18px); -webkit-backdrop-filter: blur(18px); border: 1px solid rgba(255,255,255,.15); border-radius: 20px; padding: 56px 48px 48px; max-width: 680px; width: 100%; text-align: center; box-shadow: 0 8px 32px rgba(0,0,0,.25), inset 0 1px 0 rgba(255,255,255,.1); }
    .hero-decoration { display: block; margin: 0 auto 24px; width: 64px; height: 64px; position: relative; }
    .hero-decoration svg { width: 100%; height: 100%; }
    .hero h1 { font-family: var(--font-heading); font-size: clamp(2.4rem, 6vw, 4rem); line-height: 1.1; margin-bottom: 16px; font-weight: 700; }
    .hero h1 .frost-highlight { color: var(--accent); }
    .hero-sub { font-size: clamp(1rem, 2vw, 1.15rem); color: var(--text-muted); max-width: 520px; margin: 0 auto 32px; }
    .hero-cta { display: inline-block; padding: 14px 36px; background: var(--accent); color: var(--bg-alt); font-family: var(--font-heading); font-size: 1rem; font-weight: 700; text-transform: uppercase; letter-spacing: 2px; border: none; border-radius: 8px; cursor: pointer; transition: transform .2s, box-shadow .2s; }
    .hero-cta:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(79,195,247,.3); opacity: 1; color: var(--bg-alt); }

    /* SECTIONS */
    .section { padding: 80px 0; }
    .section-dark { background: var(--bg); }
    .section-darker { background: var(--bg-alt); }
    .section-title { font-family: var(--font-heading); font-size: clamp(1.8rem, 4vw, 2.6rem); font-weight: 700; margin-bottom: 40px; text-transform: uppercase; letter-spacing: 1px; }

    /* ABOUT */
    .about-text { font-size: 1.05rem; color: var(--text-muted); max-width: 800px; line-height: 1.8; }

    /* SERVICES */
    .services-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
    .service-card { text-align: center; transition: transform .3s, border-color .3s; }
    .service-card:hover { transform: translateY(-6px); border-color: var(--accent); }
    .service-icon { color: var(--accent); margin-bottom: 16px; }
    .icon-char { font-size: 32px; line-height: 1; }
    .service-card h3 { font-family: var(--font-heading); font-size: 1.2rem; font-weight: 700; text-transform: uppercase; margin-bottom: 10px; }
    .service-card p { color: var(--text-muted); font-size: .9rem; line-height: 1.6; }
    .service-price { display: inline-block; margin-top: 14px; color: var(--accent); font-weight: 700; font-size: 1.1rem; }

    /* GALLERY */
    .gallery-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
    .gallery-item img { width: 100%; height: 200px; object-fit: cover; display: block; border-radius: 8px; border: 1px solid var(--frost-border); transition: transform .3s, border-color .3s; }
    .gallery-item img:hover { transform: scale(1.04); border-color: var(--accent); }

    /* TESTIMONIALS */
    .testimonials-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 24px; }
    .testimonial-card { transition: transform .3s; }
    .testimonial-card:hover { transform: translateY(-4px); }
    .testimonial-body p { font-size: 1rem; font-style: italic; color: var(--text-muted); line-height: 1.7; margin-bottom: 20px; }
    .testimonial-author strong { display: block; color: var(--text); font-size: 1rem; }
    .testimonial-author span { color: var(--text-muted); font-size: .85rem; }
    .testimonial-stars { margin-top: 8px; }
    .star { color: #546E7A; font-size: 1rem; }
    .star.filled { color: var(--accent); }

    /* FAQ */
    .faq-list { max-width: 800px; display: flex; flex-direction: column; gap: 16px; }
    .faq-item { transition: border-color .3s; }
    .faq-item:hover { border-color: var(--accent); }
    .faq-q { font-family: var(--font-heading); font-size: 1.15rem; font-weight: 700; text-transform: uppercase; margin-bottom: 10px; }
    .faq-a { color: var(--text-muted); line-height: 1.7; }

    /* PRICE LIST */
    .price-list { max-width: 800px; display: flex; flex-direction: column; gap: 16px; }
    .price-row { display: flex; justify-content: space-between; align-items: center; gap: 24px; }
    .price-info h3 { font-family: var(--font-heading); font-size: 1.1rem; font-weight: 700; text-transform: uppercase; margin-bottom: 4px; }
    .price-info p { color: var(--text-muted); font-size: .9rem; }
    .price-amount { font-family: var(--font-heading); font-size: 1.5rem; color: var(--accent); white-space: nowrap; font-weight: 700; }

    /* HOURS */
    .hours-list { list-style: none; max-width: 400px; }
    .hours-list li { padding: 12px 0; border-bottom: 1px solid var(--frost-border); color: var(--text-muted); font-size: .95rem; }

    /* GOOGLE REVIEWS */
    .google-reviews { background: var(--accent); padding: 16px 0; }
    .review-badge { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; }
    .review-badge .star, .review-badge .star.filled { color: var(--bg-alt); font-size: 1.2rem; }
    .review-text { color: var(--bg-alt); font-weight: 700; font-size: .95rem; }

    /* PROMO */
    .promo-banner { background: var(--bg-alt); padding: 20px 0; border-bottom: 1px solid var(--frost-border); }
    .promo-inner { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; }
    .promo-badge { background: var(--accent); color: var(--bg-alt); font-family: var(--font-heading); padding: 8px 20px; font-size: 1rem; font-weight: 700; border-radius: 6px; text-transform: uppercase; }
    .promo-content strong { display: block; margin-bottom: 2px; }
    .promo-content span { color: var(--text-muted); font-size: .9rem; }

    /* CONTACT */
    .contact-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 48px; align-items: start; }
    .contact-info { display: flex; flex-direction: column; gap: 20px; }
    .contact-item { display: flex; align-items: center; gap: 12px; color: var(--text-muted); }
    .contact-item .icon-char { color: var(--accent); flex-shrink: 0; font-size: 20px; line-height: 1; }
    .contact-item a { color: var(--text); }
    .map-link { display: inline-block; margin-top: 8px; font-weight: 600; text-transform: uppercase; font-size: .85rem; letter-spacing: 1px; }
    .contact-form { display: flex; flex-direction: column; gap: 16px; }
    .contact-form input, .contact-form textarea { background: rgba(255,255,255,.04); border: 1px solid var(--frost-border); border-radius: 8px; color: var(--text); padding: 14px 16px; font-family: var(--font-body); font-size: .95rem; outline: none; transition: border-color .2s; }
    .contact-form input:focus, .contact-form textarea:focus { border-color: var(--accent); }
    .contact-form button { padding: 14px; background: var(--accent); color: var(--bg-alt); font-family: var(--font-heading); font-size: 1rem; font-weight: 700; text-transform: uppercase; letter-spacing: 2px; border: none; border-radius: 8px; cursor: pointer; transition: transform .2s; }
    .contact-form button:hover { transform: translateY(-2px); }

    /* FOOTER */
    .footer { background: var(--bg-alt); border-top: 1px solid var(--frost-border); padding: 40px 0; text-align: center; }
    .footer p { color: var(--text-muted); font-size: .85rem; }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .nav-links { display: none; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: rgba(38,50,56,.98); backdrop-filter: blur(14px); padding: 16px 24px; gap: 16px; }
      .nav-links.show { display: flex; }
      .nav-toggle { display: block; }
      .services-grid { grid-template-columns: 1fr; }
      .gallery-grid { grid-template-columns: repeat(2, 1fr); }
      .contact-grid { grid-template-columns: 1fr; }
      .testimonials-grid { grid-template-columns: 1fr; }
      .price-row { flex-direction: column; gap: 8px; align-items: flex-start; }
    }
    @media (max-width: 480px) {
      .gallery-grid { grid-template-columns: 1fr; }
      .hero-content { padding: 100px 16px 60px; }
      .hero-glass { padding: 36px 20px 32px; }
      .hero-decoration { width: 48px; height: 48px; margin-bottom: 16px; }
      .frost-panel { padding: 20px; }
    }
  </style>
</head>
<body>
  <nav class="navbar">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${safeName}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="hero">
    ${heroImage ? `<div class="hero-bg" style="background-image:url('${escapeAttr(heroImage)}')"></div>` : ''}
    <div class="hero-overlay"></div>
    <div class="hero-content">
      <div class="hero-glass">
        <div class="hero-decoration">
          <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            <line x1="32" y1="0" x2="32" y2="64" stroke="var(--accent)" stroke-width="1" opacity=".5"/>
            <line x1="0" y1="32" x2="64" y2="32" stroke="var(--accent)" stroke-width="1" opacity=".5"/>
            <line x1="9.4" y1="9.4" x2="54.6" y2="54.6" stroke="var(--accent)" stroke-width="1" opacity=".3"/>
            <line x1="54.6" y1="9.4" x2="9.4" y2="54.6" stroke="var(--accent)" stroke-width="1" opacity=".3"/>
            <polygon points="32,4 36,16 32,12 28,16" fill="var(--accent)" opacity=".6"/>
            <polygon points="32,60 36,48 32,52 28,48" fill="var(--accent)" opacity=".6"/>
            <polygon points="4,32 16,28 12,32 16,36" fill="var(--accent)" opacity=".6"/>
            <polygon points="60,32 48,28 52,32 48,36" fill="var(--accent)" opacity=".6"/>
            <circle cx="32" cy="32" r="6" stroke="var(--accent)" stroke-width="1" fill="none" opacity=".5"/>
            <circle cx="32" cy="32" r="18" stroke="var(--accent)" stroke-width=".5" fill="none" opacity=".25"/>
          </svg>
        </div>
        <h1><span class="frost-highlight">${safeHeadline}</span></h1>
        <p class="hero-sub">${truncateAtSentence(safeAbout)}</p>
        ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="hero-cta">Get Started</a>` : ''}
      </div>
    </div>
  </section>

  ${sections.join('\n')}

  <footer class="footer">
    <div class="container">
      <p>${safeName}</p>
    </div>
  </footer>
</body>
</html>`;
  }
};
